import type { Expense, ExpenseStatus, Reimbursement } from '../../data/types';
import type { PayPeriod } from '../../data/types';
import type {
  ExpenseActionFailure,
  ExpensesState,
} from '../../data/use_expenses';

/** Queue order shown in the expense tab strip. */
export const EXPENSE_STATUSES: readonly ExpenseStatus[] = [
  'pending',
  'approved',
  'paid',
  'rejected',
] as const;

/** Count claims per queue, including queues with nothing in them. */
export function count_by_status(
  expenses: readonly Expense[],
): Record<ExpenseStatus, number> {
  const counts: Record<ExpenseStatus, number> = {
    pending: 0,
    approved: 0,
    paid: 0,
    rejected: 0,
  };
  for (const expense of expenses) {
    counts[expense.status] += 1;
  }
  return counts;
}

export function filter_by_status(
  expenses: readonly Expense[],
  status: ExpenseStatus,
): Expense[] {
  return expenses.filter((expense) => expense.status === status);
}

/** Total value of a set of claims, in minor units. Callers must ensure every claim shares one currency first (see {@link sum_amounts_by_currency}) -- summing raw minor units across DIFFERENT currencies as if they were the same unit is a real money-rules bug, not a display nicety. */
export function sum_amounts(expenses: readonly Expense[]): number {
  return expenses.reduce((total, expense) => total + expense.amount, 0);
}

/**
 * Totals a set of claims PER CURRENCY, never mixing minor units across
 * currencies (adversarial review, #22792) -- a real claim's `currency`
 * is the wire `currency_code` verbatim (see `Expense.currency`'s doc
 * comment in `types.ts`), so a queue can genuinely contain more than one
 * currency pre-approval. Order is first-seen-currency-first, so the
 * footer renders deterministically across re-renders of the same `rows`.
 */
export function sum_amounts_by_currency(
  expenses: readonly Expense[],
): Array<{ currency: string; total: number }> {
  const order: string[] = [];
  const totals = new Map<string, number>();
  for (const expense of expenses) {
    if (!totals.has(expense.currency)) {
      order.push(expense.currency);
      totals.set(expense.currency, 0);
    }
    totals.set(
      expense.currency,
      (totals.get(expense.currency) ?? 0) + expense.amount,
    );
  }
  return order.map((currency) => ({
    currency,
    total: totals.get(currency) ?? 0,
  }));
}

/**
 * The i18n key + interpolation for a claim's settlement route. Returning a key
 * rather than a string keeps this pure and translatable at the call site.
 */
export function reimbursement_label(reimbursement: Reimbursement): {
  key:
    | 'reimbursed_separately'
    | 'reimbursed_via_payroll'
    | 'reimbursed_via_payroll_pending';
  period?: PayPeriod;
} {
  if (reimbursement.via === 'separate') return { key: 'reimbursed_separately' };
  // A payroll-method claim with no resolvable period yet (not swept into a
  // pay run -- see `Reimbursement.period`'s doc comment in `types.ts`) gets
  // its OWN key rather than interpolating an empty string into
  // `reimbursed_via_payroll` -- that produced a grammatically broken string
  // in every locale (a real, permanent, common state today, since
  // `payroll.finalisePayRun` isn't OAuth-exposed yet, #22798 -- adversarial
  // review, #22792).
  if (reimbursement.period === undefined) {
    return { key: 'reimbursed_via_payroll_pending' };
  }
  return { key: 'reimbursed_via_payroll', period: reimbursement.period };
}

/** Only a pending claim can still be approved or rejected. */
export function is_actionable(expense: Expense): boolean {
  return expense.status === 'pending';
}

/**
 * What the Expenses screen should render for a given `ExpensesState` +
 * filtered row count. Extracted as a pure, independently-tested function —
 * mirrors `employee_directory_utils.ts`'s `resolve_employee_directory_view`
 * (same "ordering regression -> one unit test away from being caught"
 * rationale): `loading`/`no_entity`/`entitlement_required`/`error` MUST all
 * be checked before the `ready`-with-zero-filtered-rows branch, never the
 * other way around.
 */
export type ExpensesView =
  | 'loading'
  | 'no_entity'
  | 'entitlement_required'
  | 'error'
  | 'empty'
  | 'rows';

export function resolve_expenses_view(input: {
  state: ExpensesState;
  filtered_row_count: number;
}): ExpensesView {
  if (input.state.status === 'loading') return 'loading';
  if (input.state.status === 'no_entity') return 'no_entity';
  if (input.state.status === 'entitlement_required') {
    return 'entitlement_required';
  }
  if (input.state.status === 'error') return 'error';
  // input.state.status === 'ready'
  return input.filtered_row_count === 0 ? 'empty' : 'rows';
}

/**
 * The i18n key for a human-readable explanation of an
 * `expenseClaim.approve`/`.reject` failure — never a raw error toast. See
 * `use_expenses.ts`'s `ExpenseActionFailure` docblock for what each case
 * means.
 */
export function expense_action_error_key(
  failure: ExpenseActionFailure,
): string {
  switch (failure) {
    case 'entitlement_required':
      return 'expenses.action_error_entitlement_required';
    case 'forbidden':
      return 'expenses.action_error_forbidden';
    case 'fx_rate_required':
      return 'expenses.action_error_fx_rate_required';
    case 'error':
      return 'expenses.action_error_generic';
  }
}
